import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCustomer } from '../../context/CustomerContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Package, Search, ChevronRight, Clock, MapPin, 
  Truck, CheckCircle, XCircle, RotateCcw 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountOrders = () => {
  const { getAuthHeaders } = useCustomer();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchOrders();
  }, [page]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customer/orders?page=${page}&limit=10`, {
        headers: getAuthHeaders()
      });
      setOrders(response.data.orders || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing': return <Clock className="h-4 w-4" />;
      case 'shipped': return <Truck className="h-4 w-4" />;
      case 'delivered': return <CheckCircle className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      case 'return_requested': return <RotateCcw className="h-4 w-4" />;
      default: return <Package className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending_payment: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-blue-100 text-blue-800',
      processing: 'bg-blue-100 text-blue-800',
      packed: 'bg-indigo-100 text-indigo-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      return_requested: 'bg-orange-100 text-orange-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-stone-100 text-stone-800';
  };

  const filteredOrders = orders.filter(order => 
    order.order_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="account-orders">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-serif text-stone-800">My Orders</h2>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
        <Input
          placeholder="Search by order number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="order-search-input"
        />
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="h-16 w-16 mx-auto mb-4 text-stone-300" />
            <h3 className="text-lg font-medium text-stone-800 mb-2">No orders found</h3>
            <p className="text-stone-500 mb-4">
              {searchQuery ? 'Try a different search term' : "You haven't placed any orders yet"}
            </p>
            {!searchQuery && (
              <Link to="/shop/all">
                <Button className="bg-amber-700 hover:bg-amber-800">
                  Start Shopping
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <Card key={order.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-0">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-semibold text-stone-800 text-lg">
                        {order.order_number}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-stone-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {new Date(order.created_at).toLocaleDateString()}
                        </span>
                        {order.items?.length && (
                          <span>{order.items.length} item(s)</span>
                        )}
                      </div>
                    </div>
                    <Badge className={`${getStatusColor(order.order_status)} flex items-center gap-1`}>
                      {getStatusIcon(order.order_status)}
                      {order.order_status?.replace('_', ' ')}
                    </Badge>
                  </div>

                  {/* Order Items Preview */}
                  <div className="bg-stone-50 rounded-lg p-4 mb-4">
                    {order.items?.slice(0, 2).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-b-0">
                        <div>
                          <p className="font-medium text-stone-700">{item.product_name}</p>
                          <p className="text-sm text-stone-500">Qty: {item.quantity}</p>
                        </div>
                        <p className="font-medium text-stone-800">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                    {order.items?.length > 2 && (
                      <p className="text-sm text-stone-500 pt-2">
                        +{order.items.length - 2} more item(s)
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      {order.tracking_number && (
                        <p className="text-sm text-stone-500 flex items-center gap-1">
                          <Truck className="h-4 w-4" />
                          Tracking: {order.tracking_number}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-lg font-bold text-stone-800">
                        ${order.total?.toFixed(2)}
                      </p>
                      <Link to={`/account/orders/${order.id}`}>
                        <Button variant="outline" size="sm">
                          View Details <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-stone-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default AccountOrders;
